import { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Checkbox,
    Stack,
    CircularProgress,
    TextField,
    Divider,
    Select,
    FormControl,
    InputLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import SortIcon from "@mui/icons-material/Sort";
import Close from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import CustomTableContainer from "../../../Layout/CustomTableContainer";
import { tokens } from "../../../theme/theme";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";
import useJumboWiseProductionDetails from "../../../hooks/production/useJumboWiseProductionDetails";

// Stage groups for better organization - mapped to API response structure
const PRODUCTION_STAGES = {
    "Jumbo": ["Production", "Repulp","WIP"],
    "Rewinder": ["G1", "SL", "Buffer", "Repulp", "WIP"],
    "Reel": ["G1", "SL", "Buffer", "WIP"],
    "Sheet": ["G1", "SL", "Buffer","Repulp", "WIP"],
    "Ream": ["G1", "SL", "Repulp", "WIP"],
    "Bundle": ["G1", "SL", "Repulp", "WIP"],
    "Ream Pallet": ["G1", "SL", "Repulp", "WIP"],
    "FG": ["G1", "SL", "Repulp", "WIP"],
    "Repulp": ["Jumbo", "Rewinder"],
    "Repulp Percentage": ["Jumbo", "Rewinder"],
};

export default function JumboWiseReport({ handleBackWinderMachineEntry }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const mode = theme.palette.mode;

    // Use Jumbo Wise Production Details hook
    const { jumboWiseData, fetchJumboWiseDetails, RefreshData, loading, error } = useJumboWiseProductionDetails();

    const [rows, setRows] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [fromDate, setFromDate] = useState(() =>
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            .toISOString()
            .slice(0, 10)
    );
    const [toDate, setToDate] = useState(() =>
        new Date().toISOString().slice(0, 10)
    );

    // Sorting and column visibility
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    // Filter state
    const [filters, setFilters] = useState({
        date: "",
        jumboNo: "",
        quality: "",
        gsm: "",
    });

    // Column visibility state - all production stages visible by default
    const [visibleColumns, setVisibleColumns] = useState({
        "Date":true,
        "Jumbo No": true,
        Quality: true,
        GSM: true,
        "Jumbo": true,
        "Rewinder": true,
        Reel: true,
        Sheet: true,
        Ream: true,
        Bundle: true,
        "Ream Pallet": true,
        FG: true,
        Repulp: true,
        "Repulp Percentage":true,
    });

    // Table colors
    const tableColors = {
        headerBg: mode === "dark" ? theme.palette.primary[600] || "#1F2A40" : theme.palette.grey[100],
        stageGroup: mode === "dark" ? theme.palette.orangeAccent?.[400] || "#ff8a46" : theme.palette.yellowAccent?.[100] || "#fff7cc",
        jumboCol: mode === "dark" ? theme.palette.blueAccent?.[800] || "#1565c0" : theme.palette.blueAccent?.[100] || "#bbdefb",
        totalCol: mode === "dark" ? theme.palette.greenAccent?.[400] || "#3da58a" : theme.palette.greenAccent?.[100] || "#dbf5ee",
        rowBg: mode === "dark" ? theme.palette.grey[800] : "#fafafa",
    };

    // Process API data and transform to table format
    useEffect(() => {
        if (!loading && jumboWiseData && jumboWiseData.length > 0) {
            try {
                // Transform API data to table row format
                const transformedRows = jumboWiseData.map(item => {
                    const row = {
                        "Date": item.jumbo_date || "",
                        "Jumbo No": item.jumbo_no || "",
                        Quality: item.quality || "",
                        GSM: item.gsm || "",
                        Date: item.jumbo_date || item.shift_date || "",
                    };

                    // Map Jumbo Production stage (production_qty, wip_qty)
                    row["Jumbo-Production"] = item.production_qty || 0;
                    row["Jumbo-Repulp"] = item.jumbo_re_pulp_qty || 0;
                    row["Jumbo-WIP"] = item.wip_qty || 0;

                    // Map Child Reel stage (cr_*)
                    row["Rewinder-G1"] = item.cr_g1_qty || 0;
                    row["Rewinder-SL"] = item.cr_sl_qty || 0;
                    row["Rewinder-Buffer"] = item.cr_buffer_qty || 0;
                    row["Rewinder-Repulp"] = item.cr_repulp_qty || 0;
                    row["Rewinder-WIP"] = item.cr_wip_qty || 0;

                    // Map Rewinder stage (rw_*)
                    row["Reel-G1"] = item.rw_g1_qty || 0;
                    row["Reel-SL"] = item.rw_sl_qty || 0;
                    row["Reel-Buffer"] = item.rw_buffer_qty || 0;
                    row["Reel-WIP"] = item.rw_wip_qty || 0;

                    // Map Pallet stage (pallet_*)
                    row["Sheet-G1"] = item.pallet_g1_qty || 0;
                    row["Sheet-SL"] = item.pallet_sl_qty || 0;
                    row["Sheet-Buffer"] = item.pallet_buffer_qty || 0;
                    row["Sheet-Repulp"] = item.pallet_repulp_qty || 0;
                    row["Sheet-WIP"] = item.pallet_wip_qty || 0;

                    // Map Ream stage (ream_*)
                    row["Ream-G1"] = item.ream_g1_qty || 0;
                    row["Ream-SL"] = item.ream_sl_qty || 0;
                    row["Ream-Repulp"] = item.ream_repulp_qty || 0;
                    row["Ream-WIP"] = item.ream_wip_qty || 0;

                    // Map Bundle stage (bundle_*)
                    row["Bundle-G1"] = item.bundle_g1_qty || 0;
                    row["Bundle-SL"] = item.bundle_sl_qty || 0;
                    row["Bundle-Repulp"] = item.bundle_repulp_qty || 0;
                    row["Bundle-WIP"] = item.bundle_wip_qty || 0;

                    // Map Ream Pallet stage (ream_pallet_*)
                    row["Ream Pallet-G1"] = item.ream_pallet_g1_qty || 0;
                    row["Ream Pallet-SL"] = item.ream_pallet_sl_qty || 0;
                    row["Ream Pallet-Repulp"] = item.ream_pallet_repulp_qty || 0;
                    row["Ream Pallet-WIP"] = item.ream_pallet_wip_qty || 0;

                    // Map FG (Finished Goods) stage (fg_*)
                    row["FG-G1"] = item.fg_g1_qty || 0;
                    row["FG-SL"] = item.fg_sl_qty || 0;
                    row["FG-Repulp"] = item.fg_repulp_qty || 0;
                    row["FG-WIP"] = item.fg_wip_qty || 0;

                    // Map Repulp stage
                    row["Repulp-Jumbo"] = item.repulp_jumbo || 0;
                    row["Repulp-Rewinder"] = item.repulp_rewinder || 0;

                    // Map Repulp Percentage
                    row["Repulp Percentage-Jumbo"] = ((item.repulp_jumbo / item.production_qty)*100) || 0;
                    row["Repulp Percentage-Rewinder"] = ((item.repulp_rewinder)/(item.cr_g1_qty + item.rw_sl_qty)*100) || 0;

                    return row;
                });

                console.log("API data transformed:", transformedRows.length, "rows");
                console.log("Sample row:", transformedRows[0]);
                setRows(transformedRows);

                // Extract unique dates
                const dateSet = new Set(transformedRows.map(r => r.Date).filter(d => d));
                setAvailableDates(Array.from(dateSet).sort());
            } catch (err) {
                console.error("Error transforming API data:", err);
                setRows([]);
            }
        } else if (!loading && (!jumboWiseData || jumboWiseData.length === 0)) {
            setRows([]);
        }
    }, [jumboWiseData, loading]);

    /* Sorting */
    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sorted = [...rows].sort((a, b) => {
            const aVal = a[key];
            const bVal = b[key];

            const aNum = typeof aVal === "string" ? Number(aVal) : aVal;
            const bNum = typeof bVal === "string" ? Number(bVal) : bVal;

            if (!isNaN(aNum) && !isNaN(bNum)) {
                return direction === "asc" ? aNum - bNum : bNum - aNum;
            }
            if (typeof aVal === "string" && typeof bVal === "string") {
                return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return 0;
        });

        setRows(sorted);
    };

    /* Column visibility */
    const handleColumnToggle = (name) => {
        setVisibleColumns((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const handleColumnMenuOpen = (e) => setColumnMenuAnchor(e.currentTarget);
    const handleColumnMenuClose = () => setColumnMenuAnchor(null);

    /* Filter rows based on filter values */
    const getFilteredRows = () => {
        return rows.filter(row => {
            const dateMatch = !filters.date || (row["Date"] && row["Date"].toString() === filters.date);
            const jumboNoMatch = !filters.jumboNo || (row["Jumbo No"] && row["Jumbo No"].toString() === filters.jumboNo);
            const qualityMatch = !filters.quality || (row.Quality && row.Quality.toString() === filters.quality);
            const gsmMatch = !filters.gsm || (row.GSM && row.GSM.toString() === filters.gsm);

            return dateMatch && jumboNoMatch && qualityMatch && gsmMatch;
        });
    };

    const filteredRows = getFilteredRows();

    /* Get unique values for filter dropdowns */
    const getUniqueDates = () => {
        const dates = rows.map(r => r["Date"]).filter(d => d && d.toString().trim() !== "");
        return [...new Set(dates)].sort();
    };

    const getUniqueJumboNos = () => {
        const jumboNos = rows.map(r => r["Jumbo No"]).filter(j => j && j.toString().trim() !== "");
        return [...new Set(jumboNos)].sort();
    };

    const getUniqueQualities = () => {
        const qualities = rows.map(r => r.Quality).filter(q => q && q.toString().trim() !== "");
        return [...new Set(qualities)].sort();
    };

    const getUniqueGSMs = () => {
        const gsms = rows.map(r => r.GSM).filter(g => g && g.toString().trim() !== "");
        return [...new Set(gsms)].sort((a, b) => Number(a) - Number(b));
    };

    /* Get visible columns for a stage */
    const getVisibleStageColumns = (stage) => {
        if (!visibleColumns[stage]) return [];
        return PRODUCTION_STAGES[stage] || [];
    };

    /* Export Excel */
    const handleExportExcel = () => {
        setExportLoading(true);
        try {
            const headerRow = [];
            const numericColumnIndices = [];
            let colIndex = 0;

            // First level headers
            headerRow.push("Date");
            colIndex++;
            headerRow.push("Jumbo No");
            colIndex++;
            if (visibleColumns.Quality) {
                headerRow.push("Quality");
                colIndex++;
            }
            if (visibleColumns.GSM) {
                headerRow.push("GSM");
                colIndex++;
            }

            Object.keys(PRODUCTION_STAGES).forEach(stage => {
                if (visibleColumns[stage]) {
                    const substages = PRODUCTION_STAGES[stage];
                    substages.forEach(substage => {
                        headerRow.push(`${stage}-${substage}`);
                        numericColumnIndices.push(colIndex);
                        colIndex++;
                    });
                }
            });

            const dataRows = rows.map(r => {
                const row = [r["Date"] || ""];
                row.push(r["Jumbo No"] || "");
                if (visibleColumns.Quality) row.push(r.Quality || "");
                if (visibleColumns.GSM) row.push(r.GSM || "");

                Object.keys(PRODUCTION_STAGES).forEach(stage => {
                    if (visibleColumns[stage]) {
                        const substages = PRODUCTION_STAGES[stage];
                        substages.forEach(substage => {
                            const key = `${stage}-${substage}`;
                            row.push(Number(r[key] || 0));
                        });
                    }
                });

                return row;
            });

            const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
            ws["!cols"] = Array(headerRow.length).fill({ wch: 14 });

            // Format numeric columns as numbers with 2 decimal places
            numericColumnIndices.forEach(colIdx => {
                const colLetter = XLSX.utils.encode_col(colIdx);
                // Format data rows as numbers with 2 decimal format
                for (let rowIdx = 0; rowIdx < dataRows.length; rowIdx++) {
                    const cellRef = `${colLetter}${rowIdx + 2}`;
                    if (ws[cellRef] !== undefined) {
                        ws[cellRef] = {
                            v: ws[cellRef].v || dataRows[rowIdx][colIdx],
                            t: 'n',
                            z: '0.00'
                        };
                    }
                }
            });

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Jumbo Report");
            XLSX.writeFile(wb, `JumboWiseReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setExportLoading(false);
        }
    };

    /* Export PDF */
    const handleExportPDF = () => {
        setExportLoading(true);
        try {
            const doc = new jsPDF("l", "mm", "a4");
            const pageWidth = doc.internal.pageSize.width;

            doc.setFontSize(14);
            doc.text("Jumbo Wise Production Report", pageWidth / 2, 12, { align: "center" });
            doc.setFontSize(9);
            doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 18, { align: "center" });

            const headerRow = [];
            headerRow.push("Jumbo No");
            if (visibleColumns.Quality) headerRow.push("Quality");
            if (visibleColumns.GSM) headerRow.push("GSM");

            Object.keys(PRODUCTION_STAGES).forEach(stage => {
                if (visibleColumns[stage]) {
                    const substages = PRODUCTION_STAGES[stage];
                    substages.forEach(substage => {
                        headerRow.push(`${stage}\n${substage}`);
                    });
                }
            });

            const bodyRows = rows.map(r => {
                const row = [r["Jumbo No"] || ""];
                if (visibleColumns.Quality) row.push(r.Quality || "");
                if (visibleColumns.GSM) row.push(r.GSM || "");

                Object.keys(PRODUCTION_STAGES).forEach(stage => {
                    if (visibleColumns[stage]) {
                        const substages = PRODUCTION_STAGES[stage];
                        substages.forEach(substage => {
                            const key = `${stage}-${substage}`;
                            row.push(Number(r[key] || 0).toFixed(2));
                        });
                    }
                });

                return row;
            });

            doc.autoTable({
                head: [headerRow],
                body: bodyRows,
                startY: 24,
                theme: "grid",
                headStyles: {
                    fillColor: mode === "dark" ? [31, 42, 64] : [179, 229, 252],
                    textColor: mode === "dark" ? 255 : 0,
                    fontStyle: "bold",
                    fontSize: 8,
                    halign: "center",
                    valign: "middle",
                },
                bodyStyles: {
                    fillColor: mode === "dark" ? [33, 33, 33] : [250, 250, 250],
                    textColor: mode === "dark" ? 255 : 0,
                    fontSize: 8,
                    halign: "right",
                },
                alternateRowStyles: {
                    fillColor: mode === "dark" ? [55, 55, 55] : [255, 255, 255],
                },
                margin: { left: 8, right: 8, top: 24 },
                didDrawPage: (data) => {
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${pageCount}`,
                        pageWidth / 2,
                        doc.internal.pageSize.height - 8,
                        { align: "center" }
                    );
                },
            });

            doc.save(`JumboWiseReport_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (error) {
            console.error("PDF export failed:", error);
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header with filters and actions */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box component="h2" sx={{ m: 0 }}>
                        Jumbo Wise Production Report
                    </Box>
                    <TextField
                        type="date"
                        size="small"
                        label="From"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label="To"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => {
                            fetchJumboWiseDetails(fromDate, toDate);
                        }}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={18} /> : "Apply"}
                    </Button>
                    <Tooltip title="Refresh Data">
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={() => {
                                RefreshData(fromDate, toDate);
                            }}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Export Excel">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportExcel}
                            disabled={exportLoading}
                        >
                            {exportLoading ? <CircularProgress size={18} /> : "Excel"}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Export PDF">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportPDF}
                            disabled={exportLoading}
                        >
                            {exportLoading ? <CircularProgress size={18} /> : "PDF"}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Show / Hide Columns">
                        <IconButton size="small" onClick={handleColumnMenuOpen}>
                            <ViewWeekIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={columnMenuAnchor}
                        open={Boolean(columnMenuAnchor)}
                        onClose={handleColumnMenuClose}
                    >
                        {Object.keys(visibleColumns).map((name) => (
                            <MenuItem key={name} onClick={() => handleColumnToggle(name)}>
                                <Checkbox checked={visibleColumns[name]} size="small" />
                                {name}
                            </MenuItem>
                        ))}
                    </Menu>

                    <Tooltip title="Close">
                        <IconButton size="small" onClick={handleBackWinderMachineEntry}>
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <Divider />

            {/* Filter Row with Dropdowns */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 2, flexWrap: "wrap", alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Filter by Date</InputLabel>
                    <Select
                        value={filters.date}
                        onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                        label="Filter by Date"
                    >
                        <MenuItem value="">All Dates</MenuItem>
                        {getUniqueDates().map((date) => (
                            <MenuItem key={date} value={date}>
                                {date}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Filter by Jumbo No</InputLabel>
                    <Select
                        value={filters.jumboNo}
                        onChange={(e) => setFilters({ ...filters, jumboNo: e.target.value })}
                        label="Filter by Jumbo No"
                    >
                        <MenuItem value="">All Jumbo Nos</MenuItem>
                        {getUniqueJumboNos().map((jumboNo) => (
                            <MenuItem key={jumboNo} value={jumboNo}>
                                {jumboNo}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Filter by Quality</InputLabel>
                    <Select
                        value={filters.quality}
                        onChange={(e) => setFilters({ ...filters, quality: e.target.value })}
                        label="Filter by Quality"
                    >
                        <MenuItem value="">All Qualities</MenuItem>
                        {getUniqueQualities().map((quality) => (
                            <MenuItem key={quality} value={quality}>
                                {quality}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Filter by GSM</InputLabel>
                    <Select
                        value={filters.gsm}
                        onChange={(e) => setFilters({ ...filters, gsm: e.target.value })}
                        label="Filter by GSM"
                    >
                        <MenuItem value="">All GSMs</MenuItem>
                        {getUniqueGSMs().map((gsm) => (
                            <MenuItem key={gsm} value={gsm}>
                                {gsm}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() => setFilters({ date: "", jumboNo: "", quality: "", gsm: "" })}
                >
                    Clear Filters
                </Button>
            </Box>
            <Divider />

            {/* Table */}
            <Box sx={{ overflowX: "auto" }}>
                <CustomTableContainer colors={colors}>
                    {loading ? (
                        <SkeletonTableLoader rows={8} columns={12} />
                    ) : filteredRows.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                            {rows.length === 0 ? "No production data available. Try adjusting the date range or refresh the data." : "No results match your filters. Try adjusting the filter criteria."}
                        </Box>
                    ) : (
                        <Table sx={{ minWidth: 1400 }}>
                            <TableHead>
                                {/* Stage group header row */}
                                <TableRow sx={{ bgcolor: tableColors.headerBg }}>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell />
                                    <TableCell />
                                    {Object.keys(PRODUCTION_STAGES).map(stage => (
                                        visibleColumns[stage] ? (
                                            <TableCell
                                                key={stage}
                                                colSpan={PRODUCTION_STAGES[stage].length}
                                                align="center"
                                                sx={{ bgcolor: tableColors.stageGroup, fontWeight: 600 }}
                                            >
                                                {stage}
                                            </TableCell>
                                        ) : null
                                    ))}
                                </TableRow>

                                {/* Column headers */}
                                <TableRow sx={{ bgcolor: tableColors.headerBg }}>
                                     <TableCell sx={{ fontWeight: 600 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                           Date
                                            <IconButton size="small" onClick={() => handleSort("Date")}>
                                                <SortIcon fontSize="inherit" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            Jumbo No
                                            <IconButton size="small" onClick={() => handleSort("Jumbo No")}>
                                                <SortIcon fontSize="inherit" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>

                                    {visibleColumns.Quality && (
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                Quality
                                                <IconButton size="small" onClick={() => handleSort("Quality")}>
                                                    <SortIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    )}

                                    {visibleColumns.GSM && (
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                GSM
                                                <IconButton size="small" onClick={() => handleSort("GSM")}>
                                                    <SortIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    )}

                                    {Object.keys(PRODUCTION_STAGES).map(stage => (
                                        visibleColumns[stage] ? (
                                            PRODUCTION_STAGES[stage].map(substage => (
                                                <TableCell key={`${stage}-${substage}`} sx={{ fontWeight: 500, fontSize: "0.85rem" }}>
                                                    {substage}
                                                </TableCell>
                                            ))
                                        ) : null
                                    ))}
                                </TableRow>

                                {/* Summary Header Row - Column Totals */}
                                {filteredRows.length > 0 && (
                                    <TableRow
                                        sx={{
                                            bgcolor: tableColors.totalCol,
                                            fontWeight: 700,
                                            borderTop: `2px solid ${mode === "dark" ? "#666" : "#ccc"}`,
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                                        {visibleColumns["Jumbo No"] && <TableCell />}
                                        {visibleColumns.Quality && <TableCell />}
                                        {visibleColumns.GSM && <TableCell />}

                                        {Object.keys(PRODUCTION_STAGES).map(stage => (
                                            visibleColumns[stage] ? (
                                                PRODUCTION_STAGES[stage].map(substage => {
                                                    const columnTotal = filteredRows.reduce((sum, row) => {
                                                        return sum + (Number(row[`${stage}-${substage}`] || 0));
                                                    }, 0);

                                                    return (
                                                        <TableCell
                                                            key={`total-${stage}-${substage}`}
                                                            sx={{ fontWeight: 700 }}
                                                            align="right"
                                                        >
                                                            {columnTotal.toFixed(2)}
                                                        </TableCell>
                                                    );
                                                })
                                            ) : null
                                        ))}
                                    </TableRow>
                                )}
                            </TableHead>

                            <TableBody>
                                {filteredRows.map((row, idx) => (
                                    <TableRow
                                        key={idx}
                                        sx={{
                                            bgcolor: tableColors.rowBg,
                                            "&:hover": {
                                                bgcolor: mode === "dark" ? theme.palette.primary[500] : theme.palette.grey[200],
                                            },
                                        }}
                                    >
                                           <TableCell
                                            sx={{
                                                bgcolor: tableColors.jumboCol,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {row["Date"]}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                bgcolor: tableColors.jumboCol,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {row["Jumbo No"]}
                                        </TableCell>

                                        {visibleColumns.Quality && (
                                            <TableCell>
                                                {row.Quality || "-"}
                                            </TableCell>
                                        )}

                                        {visibleColumns.GSM && (
                                            <TableCell>
                                                {row.GSM || "-"}
                                            </TableCell>
                                        )}

                                        {Object.keys(PRODUCTION_STAGES).map(stage => (
                                            visibleColumns[stage] ? (
                                                PRODUCTION_STAGES[stage].map(substage => (
                                                    <TableCell key={`${stage}-${substage}-${idx}`} align="right">
                                                        {Number(row[`${stage}-${substage}`] || 0).toFixed(2)}
                                                    </TableCell>
                                                ))
                                            ) : null
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CustomTableContainer>
            </Box>
        </Box>
    );
}
